import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  Typography,
  TextField,
  Paper,
  IconButton,
  InputAdornment,
  Alert,
  Divider,
 
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { motion } from "framer-motion";
import { useForm, Controller } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import configuration from "../config";
import { useDispatch } from "react-redux";
import { setCredentials } from "../features/counter/Authslice.js";
import { jwtDecode } from "jwt-decode";

const SubUserLoginComponent = () => {
  
  const [showLoginCode, setShowLoginCode] = useState(false);
  const [error, setError] = useState("");
  const { API_BASE_URL } = configuration;
  const navigate = useNavigate();
  
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const { control, handleSubmit } = useForm({
    defaultValues: {
      prefix: "",
      loginCode: "",
    },
  });

  const navigateBasedOnRole = (userRole) => {
    const roleRoutes = {
      admin: "/superadmin",
      ca: "/Cadashboard",
      cs: "/csdashboard",
      user: "/kycForm",
      lawyer: "/Legal",
      employee: "/Dashboard",
      company: "/Dashboard",
      LLP: "/Dashboard",
    };

    const targetRoute = roleRoutes[userRole] || "/Dashboard";
  

    navigate(targetRoute, { replace: true });
  };

  const handleSubUserLogin = async (data) => {
    setLoading(true);
    setError("");

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/v1/subuser-login`,
        {
          prefix: data.prefix,
          loginCode: data.loginCode,
        },
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
        }
      );

     

      if (response.data?.success) {
        const { token, subUser } = response.data.data;

        // Store token in localStorage first
        localStorage.setItem("accessToken", token);

        const decodedToken = jwtDecode(token);

        const user = {
          id: decodedToken.id,
          email: decodedToken.email,
          phone: decodedToken.phone,
          role: decodedToken.role || subUser.role,
          type: decodedToken.type,
          parentUserId: decodedToken.parentUserId,
          permissions: decodedToken.permissions,
          subUserPrefix: subUser.subUserPrefix,
          loginCode: subUser.loginCode,
          parentUserPrefix: subUser.parentUserPrefix,
          isSubUser: true,

          username: decodedToken.username || subUser.username || null,
        };

        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        dispatch(
          setCredentials({
            token,
            user,
            isAuthenticated: true,
          })
        );

        await new Promise((resolve) => setTimeout(resolve, 100));

        navigateBasedOnRole(user.role);
      } else {
        throw new Error(response.data?.message || "Login failed");
      }
    } catch (error) {
      console.error("Sub-user login error:", error);

      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      delete axios.defaults.headers.common["Authorization"];

      setError(
        error.response?.data?.message ||
          error.message ||
          "Authentication failed. Please check your credentials."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    delete axios.defaults.headers.common["Authorization"];
  }, []);

  return (
    <Container
      maxWidth="xs"
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        height: "100vh",
      }}
    >
      <Paper
        elevation={12}
        sx={{
          p: 4,
          borderRadius: 3,
          textAlign: "center",
        }}
        component={motion.div}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: "medium" }}>
            Welcome back. We're glad to see you again.
          </Typography>
        </Box>

        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          Enter your prefix and login code to access your account
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit(handleSubUserLogin)}>
          <Controller
            name="prefix"
            control={control}
            rules={{
              required: "Prefix is required",
              minLength: {
                value: 2,
                message: "Prefix must be at least 2 characters",
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                fullWidth
                label="Sub-User Prefix"
                variant="outlined"
                margin="normal"
                error={!!error}
                helperText={error?.message}
                autoComplete="username"
                placeholder="e.g., SUB001"
              />
            )}
          />

          <Controller
            name="loginCode"
            control={control}
            rules={{
              required: "Login code is required",
              minLength: {
                value: 4,
                message: "Login code must be at least 4 characters",
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                fullWidth
                label="Login Code"
                type={showLoginCode ? "text" : "password"}
                variant="outlined"
                margin="normal"
                error={!!error}
                helperText={error?.message}
                autoComplete="current-password"
                placeholder="Enter your login code"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowLoginCode(!showLoginCode)}
                        edge="end"
                      >
                        {showLoginCode ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{
              mt: 3,
              py: 1.5,
              color: "black",
              backgroundColor: " theme.palette.primary.main",
              "&:hover": {
                backgroundColor: "theme.palette.primary.dark",
              },
            }}
            disabled={loading}
          >
            {loading ? "Logging in..." : "Login"}
          </Button>
        </form>

        <Box sx={{ mt: 3 }}>
          <Divider>
            <Typography variant="body2" color="textSecondary">
              OR
            </Typography>
          </Divider>
        </Box>

        <Typography variant="body2" sx={{ mt: 2 }}>
          Don't have an account yet?
          <Link to="/login" style={{ marginLeft: 5, color: "#EB5A3C" }}>
            Please contact your head office or reach out to our support team.
          </Link>
        </Typography>
      </Paper>
    </Container>
  );
};

export default SubUserLoginComponent;
